/**
 * Try The World — Exit Intent Popup
 * ----------------------------------
 * A self-contained module that detects exit intent and displays
 * a context-aware popup. No dependencies.
 *
 * Architecture:
 *   1. Trigger Layer    — detects mouseleave (desktop) + window blur (mobile)
 *   2. Eligibility Layer — decides whether to show
 *   3. Content Layer    — decides what to show
 *   4. Persistence Layer — tracks state + analytics
 */

(function (window, document) {
  'use strict';

  // ==========================================================================
  // CONFIGURATION (override via window.TTW_EXIT_INTENT_CONFIG before load)
  // ==========================================================================

  const DEFAULTS = {
    // Trigger
    sensitivity: 20,             // px from top before trigger fires
    delayMs: 3000,               // minimum time on page before eligible
    cooldownDays: 7,             // days before showing again after dismiss
    cooldownDaysAfterConvert: 30,// longer cooldown if user already redeemed

    // Storage keys
    storageKey: 'ttw_exit_intent_state',
    sessionKey: 'ttw_exit_intent_session',

    // Optional logo image rendered above the headline
    logoUrl: "https://cdn.shopify.com/s/files/1/0575/4932/4427/files/ttw_logo_teal.png?v=1676651655",

    // Page detection — match against pathname.
    // Only these paths are eligible; everything else is skipped.
    pageRules: {
      cart:     { test: (p) => /^\/cart(\/|$)/i.test(p),              offer: 'cart' },
      orderNow: { test: (p) => /^\/pages\/order-now(\/|$)/i.test(p),  offer: 'orderNow' }
    },

    // Offers — content layer reads from here
    offers: {
      cart: {
        eyebrow: 'Wait — your cart is still warm',
        headline: "Don't Miss<br> 20% Off Today!",
        subhead: 'Wait! Before you go, don’t miss our one-time promotional offer of 20% any order or gift that will be applied at checkout today.',
        code: 'JOY20',
        cta: 'Apply discount',
        redirect: '/checkout'
      },
      orderNow: {
        eyebrow: 'Hold on a moment',
        headline: "Don't Miss<br> 20% Off Today!",
        subhead: 'Wait! Before you go, don’t miss our one-time promotional offer of 20% any order or gift that will be applied at checkout today.',
        code: 'JOY20',
        cta: 'Apply discount',
        redirect: '/pages/order-now'
      }
    },

    // Analytics callback — wire this to GA4, Segment, etc.
    onEvent: function (eventName, payload) {
      if (window.dataLayer) {
        window.dataLayer.push({ event: eventName, ...payload });
      }
      console.log('[ExitIntent]', eventName, payload);
    }
  };

  const config = Object.assign({}, DEFAULTS, window.TTW_EXIT_INTENT_CONFIG || {});

  // ==========================================================================
  // PERSISTENCE LAYER
  // ==========================================================================

  const Storage = {
    get() {
      try {
        const raw = localStorage.getItem(config.storageKey);
        return raw ? JSON.parse(raw) : {};
      } catch { return {}; }
    },
    set(state) {
      try {
        localStorage.setItem(config.storageKey, JSON.stringify(state));
      } catch {}
    },
    update(patch) {
      this.set(Object.assign({}, this.get(), patch));
    },
    sessionShown() {
      try { return sessionStorage.getItem(config.sessionKey) === '1'; }
      catch { return false; }
    },
    markSessionShown() {
      try { sessionStorage.setItem(config.sessionKey, '1'); } catch {}
    }
  };

  // ==========================================================================
  // ELIGIBILITY LAYER
  // ==========================================================================

  function isEligible() {
    // One per session, always
    if (Storage.sessionShown()) return false;

    const state = Storage.get();
    const now = Date.now();

    // Already converted? Use long cooldown.
    if (state.lastConverted) {
      const elapsed = now - state.lastConverted;
      if (elapsed < config.cooldownDaysAfterConvert * 86400000) return false;
    }

    // Recently dismissed? Use short cooldown.
    if (state.lastDismissed) {
      const elapsed = now - state.lastDismissed;
      if (elapsed < config.cooldownDays * 86400000) return false;
    }

    // Don't show on checkout — user is mid-purchase
    if (/\/checkouts?\//i.test(location.pathname)) return false;

    return true;
  }

  // ==========================================================================
  // CONTENT LAYER
  // ==========================================================================

  function getOfferForPage() {
    const path = location.pathname;
    for (const key of Object.keys(config.pageRules)) {
      const rule = config.pageRules[key];
      if (rule && rule.test(path)) {
        return config.offers[rule.offer];
      }
    }
    return null;
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================

  let popupEl = null;
  let pageEntryTime = Date.now();

  function injectStyles() {
    if (document.getElementById('ttw-exit-intent-styles')) return;
    const style = document.createElement('style');
    style.id = 'ttw-exit-intent-styles';
    style.textContent = `
      .ttw-ei-overlay {
        position: fixed; inset: 0; z-index: 999999;
        background: rgba(0, 0, 0, 0.5);
        opacity: 0; transition: opacity 280ms ease;
        display: flex; align-items: center; justify-content: center;
        padding: 20px;
      }
      .ttw-ei-overlay.is-open { opacity: 1; }

      .ttw-ei-modal {
        position: relative;
        width: 100%; max-width: 680px;
        background: #ffffff;
        border-radius: 10px;
        box-shadow: 0 20px 60px -10px rgba(0,0,0,0.35);
        transform: translateY(20px) scale(0.98);
        transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
        font-family: Lato, sans-serif;
        overflow: hidden;
    background-image: url(https://cdn.shopify.com/s/files/1/0575/4932/4427/files/snack-hero-sugar2_mod_1f711691-8674-4f5f-962a-62f998a2082b-min.jpg?v=1737659102);
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
      }
      .ttw-ei-overlay.is-open .ttw-ei-modal { transform: translateY(0) scale(1); }

      .ttw-ei-close {
        position: absolute; top: 10px; right: 14px;
        width: 32px; height: 32px;
        background: transparent; border: none;
        font-size: 24px; line-height: 1; cursor: pointer;
        color: #888; opacity: 0.7;
        transition: opacity 150ms ease;
      }
      .ttw-ei-close:hover { opacity: 1; }

      .ttw-ei-body { padding: 50px 48px 40px; text-align: center; }

      .ttw-ei-logo {
        display: block; margin: 0 auto 22px;
        max-width: 175px; height: auto;
      }

      .ttw-ei-eyebrow {
        font-family: Lato, sans-serif;
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        font-weight: 700;
        color: #0acea8; margin: 0 0 12px;
      }
      .ttw-ei-headline {
        font-family: Lato, sans-serif;
        font-size: 45px; line-height: 1.1; font-weight: 600;
        text-transform: uppercase;
        color: #000; margin: 0;
      }
      .ttw-ei-subhead {
        font-family: Lato, sans-serif;
        font-size: 16px; line-height: 1.5;
        color: rgba(18, 18, 18, 0.75);
        max-width: 400px; margin: 20px auto 0;
      }

      .ttw-ei-form { display: flex; flex-direction: column; gap: 12px; margin-top: 20px; }

      .ttw-ei-cta {
            width: 70%;
    padding: 26px 24px;
        font-family: Lato, sans-serif;
        font-size: 14px; font-weight: 700;
        letter-spacing: 0.06rem; text-transform: uppercase;
        background: #0acea8; color: #ffffff;
        border: none; border-radius: 4px;
        cursor: pointer;
        transition: background 150ms ease;
        margin:auto;
      }
      .ttw-ei-cta:hover { background: #08b896; }
      .ttw-ei-cta:disabled { opacity: 0.6; cursor: wait; }

      .ttw-ei-footnote { margin: 15px 0 0; }
      .ttw-ei-decline {
        background: transparent; border: none;
        font-family: Lato, sans-serif;
        font-size: 12px; text-transform: uppercase;
        letter-spacing: 0.06rem;
        color: #b5b5b5; padding: 10px 0;
        cursor: pointer;
        transition: color 150ms ease;
      }
      .ttw-ei-decline:hover { color: #888; }

      @media (max-width: 768px) {
        .ttw-ei-body { padding: 32px 20px 28px; }
        .ttw-ei-logo { margin-bottom: 18px; }
        .ttw-ei-headline { font-size: 35px; }
        .ttw-ei-subhead { font-size: 0.9em; margin-top: 15px; }
        .ttw-ei-form { margin-top: 18px; }
        .ttw-ei-cta { font-size: 0.9em; padding: 20px 16px; }
      }
    `;
    document.head.appendChild(style);
  }

  function render(offer) {
    injectStyles();

    const overlay = document.createElement('div');
    overlay.className = 'ttw-ei-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'ttw-ei-headline');

    const logoMarkup = config.logoUrl
      ? `<img src="${config.logoUrl}" alt="" class="ttw-ei-logo">`
      : '';

    overlay.innerHTML = `
      <div class="ttw-ei-modal">
        <button class="ttw-ei-close" aria-label="Close">×</button>
        <div class="ttw-ei-body">
          ${logoMarkup}
          
          <h2 id="ttw-ei-headline" class="ttw-ei-headline">${offer.headline}</h2>
          <p class="ttw-ei-subhead">${offer.subhead}</p>
          <form class="ttw-ei-form">
            <button type="submit" class="ttw-ei-cta">${offer.cta}</button>
          </form>
          <p class="ttw-ei-footnote">
            <button type="button" class="ttw-ei-decline">No Thanks</button>
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    popupEl = overlay;

    // Force reflow then animate in
    requestAnimationFrame(() => overlay.classList.add('is-open'));

    // Wire events
    overlay.querySelector('.ttw-ei-close').addEventListener('click', () => dismiss('close-button'));
    overlay.querySelector('.ttw-ei-decline').addEventListener('click', () => dismiss('decline-link'));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss('overlay-click'); });
    document.addEventListener('keydown', escHandler);

    overlay.querySelector('.ttw-ei-form').addEventListener('submit', (e) => {
      e.preventDefault();
      handleConvert(offer, overlay);
    });

    config.onEvent('exit_intent_shown', { page: location.pathname, code: offer.code });
  }

  function escHandler(e) {
    if (e.key === 'Escape' && popupEl) dismiss('escape-key');
  }

  function dismiss(reason) {
    if (!popupEl) return;
    Storage.update({ lastDismissed: Date.now() });
    config.onEvent('exit_intent_dismissed', { reason, page: location.pathname });
    closePopup();
  }

  function closePopup() {
    if (!popupEl) return;
    popupEl.classList.remove('is-open');
    document.removeEventListener('keydown', escHandler);
    setTimeout(() => {
      if (popupEl && popupEl.parentNode) popupEl.parentNode.removeChild(popupEl);
      popupEl = null;
    }, 300);
  }

  function handleConvert(offer, overlay) {
    const cta = overlay.querySelector('.ttw-ei-cta');
    cta.disabled = true;

    Storage.update({ lastConverted: Date.now() });
    config.onEvent('exit_intent_converted', { type: 'discount', code: offer.code });
    // Shopify auto-apply discount URL — redirect target depends on the offer
    window.location.href = `/discount/${offer.code}?redirect=${encodeURIComponent(offer.redirect)}`;
  }

  // ==========================================================================
  // TRIGGER LAYER
  // ==========================================================================

  function tryFire() {
    if (!isEligible()) return;
    if (Date.now() - pageEntryTime < config.delayMs) return;

    const offer = getOfferForPage();
    if (!offer) return;

    Storage.markSessionShown();
    render(offer);
  }

  function attachDesktopTrigger() {
    document.addEventListener('mouseleave', (e) => {
      // Only top-edge exits — those signal browser chrome interaction
      if (e.clientY <= config.sensitivity) tryFire();
    });
  }

  function attachMobileTrigger() {
    // Window blur — fires when focus leaves the page (address bar tap,
    // back-button tap, browser menu). Filter out app-switching by checking
    // document.hidden: if the tab is still visible, it's chrome interaction.
    window.addEventListener('blur', () => {
      if (!document.hidden) tryFire();
    });
  }

  function isMobile() {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  // ==========================================================================
  // INIT
  // ==========================================================================

  function init() {
    pageEntryTime = Date.now();
    if (isMobile()) attachMobileTrigger();
    else attachDesktopTrigger();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose minimal API for debugging / manual triggers
  window.TTWExitIntent = {
    show: () => { Storage.set({}); sessionStorage.removeItem(config.sessionKey); tryFire(); },
    reset: () => { localStorage.removeItem(config.storageKey); sessionStorage.removeItem(config.sessionKey); },
    config
  };

})(window, document);
